<template>
  <div class="auth">
    <center-layout>
      <template #heading>
        <div class="auth__heading">
          <img v-if="isB2BPlatform" :src="b2bLogo" alt="Platform logo" />
          <div v-else-if="oezPlatformName === 'Foodrazor'" class="logo-wrapper">
            <img src="@/assets/Foodrazor-logo-part1.png" alt="Foodrazor logo image" />
            <img
              class="ml-2 mt-2 height-45"
              src="@/assets/Foodrazor-logo-part2.svg"
              alt="Foodrazor logo text"
            />
          </div>
          <img v-else src="@/assets/logo-dark.svg" alt="EZ logo" />
        </div>
      </template>
      <h1 v-if="!isInvitation" class="mb-24">Sign-in Email Sent</h1>
      <h1 v-else class="mb-24">Approval Request Sent</h1>
      <p v-if="!isInvitation">
        {{
          fromShoppingCart
            ? 'To access the portal and submit your order, please go to your inbox and open the link from the email we sent you.'
            : 'To access the portal, please go to your inbox and open the link from the email we sent you.'
        }}
      </p>
      <p v-else>
        Your account request is currently pending approval from the supplier. You will be notified
        when you can place orders.
      </p>
      <ez-input
        v-if="!isInvitation"
        type="email"
        label="Email address"
        name="email"
        placeholder="email@example.com"
        validators="email|required"
        ref="emailInput"
        formKey="resendLink"
        :value="onboarding.owner.email"
        disabled
      ></ez-input>

      <ez-button v-if="!isInvitation" @click="resendLink" isFullWidth class="mt-24"
        >Resend Sign-in Email</ez-button
      >
    </center-layout>
  </div>
</template>

<script>
import EzInput from '@/components/ui/Input';
import EzButton from '@/components/ui/Button';
import CenterLayout from '@/components/layout/Center.vue';
import flash from '@/components/ui/FlashMessage';
import httpService from '@/api/http';

export default {
  components: {
    EzInput,
    EzButton,
    CenterLayout,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
    onboarding: {
      type: Object,
      required: true,
    },
    fromShoppingCart: {
      type: Boolean,
      required: false,
      default: false,
    },
    isInvitation: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {};
  },
  computed: {
    oezPlatformName() {
      return process.env.VUE_APP_PLATFORM_TITLE;
    },
    isB2BPlatform() {
      return ![
        'localhost',
        'app-dev.orderez.co',
        'app-dev.foodrazor.co',
        'app-staging.orderez.co',
        'app-staging.foodrazor.co',
        'app.orderez.co',
        'app.foodrazor.com',
      ].includes(window.location.hostname);
    },
    b2bDomain() {
      return window.location.hostname.split('.')[0];
    },
    b2bLogo() {
      return `${process.env.VUE_APP_API_URL}/public/b2b-portal/${this.b2bDomain}/logo`;
    },
  },
  methods: {
    async resendLink() {
      try {
        await httpService.put(
          `public/b2b-portal/login${this.fromShoppingCart ? '?page=shopping_cart' : ''}`,
          {
            email: this.onboarding.owner.email,
            ...(this.onboarding.venue?.id ? { venueId: this.onboarding.venue.id } : {}),
          },
        );
        flash.success({
          title: this.$t('venues.onboarding.flash.title'),
          message: this.$t('venues.onboarding.flash.message', {
            distributorName: this.distributor.name,
          }),
        });
      } catch (e) {
        console.error(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$primary-color: #4d7cfe;
$label-color: #8790a3;
$default-color: #252631;
$text-color: #6c7995;

.auth {
  width: 100%;
  height: 100%;

  h1 {
    @include font-size(24px);
    font-weight: 500;
    text-align: center;
    margin: 0;
  }
  &__heading {
    @extend %flex-center;
    justify-content: center;
    flex-wrap: wrap;
    padding: 5rem 0 1rem 0;
    margin: 0 auto;
    width: 7.75rem;
    display: block;
    span {
      @include font-size(20px);
      color: $label-color;
      margin-left: 0.5rem;
    }
    img {
      width: 100%;
      height: auto;
    }
    .logo-wrapper {
      display: flex;
      align-items: center;
      height: 100%;
    }
  }
  :deep() &__form {
    margin: 0 auto;
    h1 {
      @include font-size(24px);
      font-weight: 500;
      text-align: center;
      margin: 0;
    }
    a {
      display: block;
      color: $primary-color;
      text-align: center;
      text-decoration: none;
      margin-top: 1.5rem;
      @include font-size(14px);
    }
    p {
      @include font-size(14px);
      color: $text-color;
      text-align: center;
      line-height: 1.75;
      margin: 0.5rem 0 2rem 0;
      a {
        display: inline-block;
        color: $text-color;
        text-decoration: underline;
      }
    }
    button {
      margin-top: 1rem;
    }
    :deep() .input-group {
      width: 100%;
      + .input-group {
        margin-top: 1rem;
      }
      &__input {
        height: 2.5rem;
      }
    }
  }

  &.b2b-layout {
    padding: 48px;
    padding-top: 0;
    height: auto;
    margin: 0 auto;
    margin-top: 128px;
    display: flex;
    max-width: 1256px;
    align-items: center;

    :deep() .layout-center {
      padding-left: 48px;
      border-left: 1px dashed #e1e5ed;
      .layout-center__heading .auth__heading {
        padding: 0;
        padding-bottom: 1rem;
      }
      .layout-center__content {
        h1,
        p {
          text-align: center;
        }
      }
    }
    .b2b-content {
      margin-right: 48px;
      display: flex;
      flex-direction: column;
      align-items: center;
      flex-shrink: 1;

      p {
        font-weight: 500;
        font-size: 18px;
        line-height: 1.4;
        text-align: center;
        padding: 0 48px;
      }
      img {
        border-radius: 5px;
        width: 700px;
        max-width: 100%;
      }

      h2 {
        font-size: 32px;
        margin: 0;
        & + img {
          margin-top: 48px;
        }
        & + p {
          margin: 0;
          margin-top: 48px;
        }
      }

      img + p {
        margin: 0;
        margin-top: 48px;
      }
    }

    @media (max-width: 768px) {
      :deep() .layout-center {
        padding-left: 0;
        border-left: none;
      }
      flex-direction: column;
      .b2b-content {
        margin-right: 0;
        h2 {
          margin-bottom: 24px;
        }
        img,
        p {
          display: none;
        }
      }
    }
  }
}
</style>
